import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Edit, FileText, ExternalLink } from 'lucide-react'
import dayjs from 'dayjs'
import Paper from '@/components/Paper'
import {
    DataTableAction,
    DataTableColumn,
    DataTableCustomized,
    FilterBar,
} from '@/components/ui/data-table-customized'
import { useModal } from '@/contexts/modal'
import { useFilePreview } from '@/contexts/file-preview'
import { IPermissionProps } from '@/types/shared'
import { formatCurrency } from '@/utils/function'
import { Attachment } from '@/modules/Upload/types'
import { useSuppliers } from '../../hooks'
import type { Supplier, PartnerContract } from '../../types'

/** Convert attachments to FileItem format for preview */
const attachmentsToFileItems = (attachments: Attachment[]) =>
    attachments.map((att) => ({
        fileKey: att.attachment_file_url,
        attachmentInfo: att,
        extension: att.attachment_file_type?.split('/')[1] || '',
        isHeic:
            att.attachment_file_type?.includes('heic') ||
            att.attachment_file_url?.toLowerCase().endsWith('.heic'),
    }))

const SuppliersList: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation('contract')
    const navigate = useNavigate()
    const { setModal } = useModal()
    const { previewMultiple } = useFilePreview()

    const {
        suppliers,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
    } = useSuppliers()

    // Expanded contracts columns
    const contractColumns: DataTableColumn<PartnerContract>[] = [
        {
            key: 'contract_number_id',
            title: t('contract_number_id'),
            render: (row) =>
                row ? (
                    <span className="font-medium text-primary">
                        {row.contract_number_id}
                    </span>
                ) : null,
        },
        {
            key: 'contract_value',
            title: t('contract_value'),
            render: (row) => (row ? formatCurrency(row.contract_value) : '-'),
        },
        {
            key: 'duration',
            title: t('contract_duration'),
            width: 200,
            render: (row) => {
                if (!row) return '-'
                const from = row.contract_effective_date
                    ? dayjs(row.contract_effective_date).format('DD/MM/YYYY')
                    : '-'
                const to = row.contract_expiry_date
                    ? dayjs(row.contract_expiry_date).format('DD/MM/YYYY')
                    : '-'
                return `${from} - ${to}`
            },
        },
        {
            key: 'attachments',
            title: t('attachments'),
            render: (row) =>
                row?.attachments?.length ? (
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            previewMultiple(
                                attachmentsToFileItems(row.attachments)
                            )
                        }}
                        className="flex items-center gap-1 hover:text-primary transition-colors"
                    >
                        <FileText className="w-4 h-4" />
                        <span>{row.attachments.length}</span>
                    </button>
                ) : (
                    '-'
                ),
        },
        {
            key: 'actions',
            title: '',
            width: 60,
            render: (row) =>
                row ? (
                    <button
                        onClick={() =>
                            setModal(
                                'ContractModals/PartnerContractModal/index',
                                {
                                    partnerContract: row,
                                    mode: 'update',
                                }
                            )
                        }
                        className="p-1 hover:bg-muted rounded"
                        title={t('edit')}
                    >
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </button>
                ) : null,
        },
    ]

    // Row actions
    const rowActions: DataTableAction<Supplier>[] = [
        {
            key: 'edit',
            label: t('edit'),
            icon: <Edit className="w-4 h-4" />,
            onClick: (row: Supplier) => {
                setModal('ContractModals/SupplierModal/index', {
                    supplier: row,
                    mode: 'update',
                })
            },
        },
    ]

    return (
        <Paper className="space-y-2 mt-2">
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={suppliers}
                    rowKey={(row) => String(row?.partner_id)}
                    size="small"
                    resizable
                    loading={!suppliers.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-data')}
                    onRowClick={(row) =>
                        navigate(
                            `/contracts/partners/suppliers/${row.partner_id}`
                        )
                    }
                    actions={rowActions}
                    expandable={{
                        expandedColumns: contractColumns,
                        expandedRowData: (row) => row.contracts || [],
                        expandedRowKey: (contract) =>
                            String(contract.contract_id),
                        rowExpandable: (row) =>
                            !!(row.contracts && row.contracts.length > 0),
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 20,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-supplier'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'suppliers-list',
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default SuppliersList
